import {
  InventoryCount,
  CurrentValueResponse,
} from '../../services/inventory/inventoryService';
import {
  groupCountsIntoSessions,
  SessionGroup,
} from '../../components/pages/inventory/inventoryOverviewUtils';
import { getTableFormattedDate } from '../dateUtils';

/**
 * Pure, React-free computation for the R6 Inventory report. All inputs come from
 * the inventory store: `currentValue` (the live /current-value endpoint — NOT
 * recomputed here) drives the per-section value + staleness table, while
 * `countsHistory` is grouped into SESSIONS (via the shared `groupCountsIntoSessions`)
 * to drive the value trend, the biggest-value items currently held, and the
 * movers between the two most recent sessions per group.
 *
 * This report is inherently session-based and ignores the page time filter.
 */

const MS_PER_DAY = 86_400_000;

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Per-section current value + staleness (from the live current-value endpoint). */
export interface InventorySectionRow {
  sectionId: string | null;
  sectionName: string;
  groupName: string | null;
  value: number;
  lastCountedAt: string;
  /** Whole days since this section was last counted (>= 0). */
  daysAgo: number;
}

/** One point on the value-over-time trend chart (a session completion). */
export interface InventoryTrendPoint {
  label: string;
  timestamp: number;
  total: number;
  /** Dynamic per-group running values, keyed by the series dataKey (`g0`, `g1`, ...). */
  [seriesKey: string]: number | string;
}

/** A per-group line on the trend chart. `label` is null for ungrouped/legacy counts. */
export interface InventoryTrendSeries {
  dataKey: string;
  label: string | null;
}

/** A product currently held (latest session per group), valued at count time. */
export interface InventoryHeldItem {
  key: string;
  productName: string;
  groupName: string | null;
  value: number;
  /** Quantity in the product's standard unit. */
  quantity: number;
  /** Standard unit ('g' | 'ml' | 'p' | ...). */
  unit: string;
}

/** A product whose held value changed between a group's two most recent sessions. */
export interface InventoryMover {
  key: string;
  productName: string;
  groupName: string | null;
  beforeValue: number;
  afterValue: number;
  deltaValue: number;
}

export interface InventoryReportResult {
  hasCounts: boolean;
  sessionCount: number;
  totalCurrentValue: number;
  sections: InventorySectionRow[];
  trend: InventoryTrendPoint[];
  trendSeries: InventoryTrendSeries[];
  /** Trend needs >= 2 sessions to be meaningful. */
  hasTrend: boolean;
  heldItems: InventoryHeldItem[];
  risers: InventoryMover[];
  fallers: InventoryMover[];
  hasMovers: boolean;
}

export interface InventoryReportInputs {
  countsHistory: InventoryCount[];
  currentValue: CurrentValueResponse | null;
  /** `Date.now()` from the caller (keeps this module pure / testable). */
  now: number;
}

/** Stable bucket for "one physical group counted". Legacy/ungrouped counts collapse together. */
function groupKeyOf(session: SessionGroup): string {
  return session.groupId ?? session.groupName ?? 'ungrouped';
}

/** Sum a session's item value per inventory `productKey` (across its section counts). */
function aggregateSessionValues(
  session: SessionGroup
): Map<string, { value: number; name: string }> {
  const byProduct = new Map<string, { value: number; name: string }>();
  session.counts.forEach((count) => {
    count.items.forEach((item) => {
      const existing = byProduct.get(item.productKey);
      if (existing) existing.value += item.value;
      else byProduct.set(item.productKey, { value: item.value, name: item.productName });
    });
  });
  return byProduct;
}

export function computeInventoryReport(inputs: InventoryReportInputs): InventoryReportResult {
  const { countsHistory, currentValue, now } = inputs;

  const sessions = groupCountsIntoSessions(countsHistory); // newest session first
  const sessionCount = sessions.length;
  const hasCounts = sessionCount >= 1;

  // --- Per-section current value + staleness (from the live endpoint) ---
  const totalCurrentValue = currentValue?.totalValue ?? 0;
  const sections: InventorySectionRow[] = (currentValue?.perSection ?? [])
    .map((section) => {
      const last = new Date(section.lastCountedAt);
      const daysAgo = Math.max(0, Math.floor((now - last.getTime()) / MS_PER_DAY));
      return {
        sectionId: section.sectionId,
        sectionName: section.sectionName,
        groupName: section.groupName,
        value: section.totalValue,
        lastCountedAt: section.lastCountedAt,
        daysAgo,
      };
    })
    .sort((a, b) => b.value - a.value);

  // --- Value trend across sessions (per group + running total) ---
  const chrono = [...sessions].sort(
    (a, b) => a.completedAt.getTime() - b.completedAt.getTime()
  );

  // Distinct group buckets, in order of first appearance, each mapped to a series.
  const groupLabelByKey = new Map<string, string | null>();
  chrono.forEach((session) => {
    const key = groupKeyOf(session);
    if (!groupLabelByKey.has(key)) groupLabelByKey.set(key, session.groupName ?? null);
  });
  const groupKeys = Array.from(groupLabelByKey.keys());
  const trendSeries: InventoryTrendSeries[] = groupKeys.map((key, i) => ({
    dataKey: `g${i}`,
    label: groupLabelByKey.get(key) ?? null,
  }));
  const seriesKeyByGroup = new Map(groupKeys.map((key, i) => [key, `g${i}`]));

  const running = new Map<string, number>();
  const trend: InventoryTrendPoint[] = chrono.map((session) => {
    running.set(groupKeyOf(session), session.totalValue);
    const point: InventoryTrendPoint = {
      label: getTableFormattedDate(session.completedAt),
      timestamp: session.completedAt.getTime(),
      total: 0,
    };
    let total = 0;
    groupKeys.forEach((key) => {
      const value = running.get(key) ?? 0;
      point[seriesKeyByGroup.get(key) as string] = round2(value);
      total += value;
    });
    point.total = round2(total);
    return point;
  });
  const hasTrend = sessionCount >= 2;

  // --- Biggest-value items currently held (latest session per group) ---
  const latestByGroup = new Map<string, SessionGroup>();
  sessions.forEach((session) => {
    const key = groupKeyOf(session);
    if (!latestByGroup.has(key)) latestByGroup.set(key, session); // sessions are newest-first
  });
  const heldByKey = new Map<string, InventoryHeldItem>();
  Array.from(latestByGroup.values()).forEach((session) => {
    const groupName = session.groupName;
    session.counts.forEach((count) => {
      count.items.forEach((item) => {
        const key = `${groupKeyOf(session)}::${item.productKey}`;
        const existing = heldByKey.get(key);
        if (existing) {
          existing.value += item.value;
          existing.quantity += item.countInStdUnit;
        } else {
          heldByKey.set(key, {
            key,
            productName: item.productName,
            groupName,
            value: item.value,
            quantity: item.countInStdUnit,
            unit: item.stdUnit,
          });
        }
      });
    });
  });
  const heldItems = Array.from(heldByKey.values())
    .sort((a, b) => b.value - a.value)
    .slice(0, 20);

  // --- Movers between the two most recent sessions per group ---
  const sessionsByGroup = new Map<string, SessionGroup[]>();
  sessions.forEach((session) => {
    const key = groupKeyOf(session);
    const list = sessionsByGroup.get(key) ?? [];
    list.push(session); // newest-first preserved
    sessionsByGroup.set(key, list);
  });

  const movers: InventoryMover[] = [];
  Array.from(sessionsByGroup.entries()).forEach(([groupKey, list]) => {
    if (list.length < 2) return;
    const latest = list[0];
    const previous = list[1];
    const after = aggregateSessionValues(latest);
    const before = aggregateSessionValues(previous);
    const productKeys = new Set(
      Array.from(after.keys()).concat(Array.from(before.keys()))
    );
    Array.from(productKeys).forEach((productKey) => {
      const a = after.get(productKey);
      const b = before.get(productKey);
      const afterValue = a?.value ?? 0;
      const beforeValue = b?.value ?? 0;
      const deltaValue = afterValue - beforeValue;
      if (Math.abs(deltaValue) < 0.005) return;
      movers.push({
        key: `${groupKey}::${productKey}`,
        productName: (a ?? b)?.name ?? '',
        groupName: latest.groupName,
        beforeValue: round2(beforeValue),
        afterValue: round2(afterValue),
        deltaValue: round2(deltaValue),
      });
    });
  });

  const risers = movers
    .filter((m) => m.deltaValue > 0)
    .sort((a, b) => b.deltaValue - a.deltaValue)
    .slice(0, 10);
  const fallers = movers
    .filter((m) => m.deltaValue < 0)
    .sort((a, b) => a.deltaValue - b.deltaValue)
    .slice(0, 10);
  const hasMovers = risers.length > 0 || fallers.length > 0;

  return {
    hasCounts,
    sessionCount,
    totalCurrentValue,
    sections,
    trend,
    trendSeries,
    hasTrend,
    heldItems,
    risers,
    fallers,
    hasMovers,
  };
}
